import { alpha } from '@mui/material';
import { palette } from '@client/theme/colors';
import { radii, theme } from '@client/theme/constants';
import { typography } from '@client/theme/typography';
import { shadows } from '../shadows';
import type { ThemeOptions } from '@mui/material';

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    grey: true;
  }

  interface ChipPropsVariantOverrides {
    transparent: true;
    'two-toned': true;
  }
}

const extraVariants: any = {
  iconColorWarning: {
    color: palette.warning.main,
  },
};

const Chip: ThemeOptions['components'] = {
  MuiChip: {
    defaultProps: {
      color: 'default',
    },
    styleOverrides: {
      root: {
        fontSize: typography.body1.fontSize,
        lineHeight: typography.body1.lineHeight,
        padding: '0 10px',
        pointerEvents: 'none',
        userSelect: 'none',

        '& > svg': {
          color: 'inherit',
        },
      },
      clickable: {
        pointerEvents: 'auto',

        '&:active': {
          boxShadow: 'none',
        },
      },
      deletable: {
        pointerEvents: 'auto',
      },
      filled: {
        backgroundColor: palette.text.primary,
        color: palette.grey[50],

        '&$colorPrimary': {
          backgroundColor: palette.primary.main,
        },

        '&$colorSecondary': {
          backgroundColor: palette.secondary.main,
        },
      },
      sizeSmall: {
        padding: '0px !important',
        height: theme.spacing(3),
      },
      labelSmall: {
        fontSize: typography.overline.fontSize,
        lineHeight: typography.overline.lineHeight,
        padding: theme.spacing(1 / 2, 1),
      },
      icon: {
        backgroundColor: 'transparent',
        color: 'inherit',
      },
      iconSmall: {
        fontSize: 18,
        padding: 0,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing((1 / 2) * -1),
      },
      label: {
        maxWidth: '100%',
      },
      deleteIconSmall: {
        backgroundColor: alpha(palette.background.paper, 0.16),
        borderRadius: '100%',
        color: palette.primary.contrastText,
        height: 17,
        lineHeight: '19px',
        marginLeft: 0,
        marginRight: theme.spacing(1 / 2),
        paddingLeft: 1,
        textAlign: 'center',
        width: 17,

        '&:hover': {
          color: palette.primary.contrastText,
        },
      },
      deleteIconOutlinedColorPrimary: {
        color: palette.text.primary,

        '&:hover, &:active': {
          color: palette.grey[600],
        },
      },
      deleteIconOutlinedColorInfo: {
        color: palette.grey[500],
        backgroundColor: 'transparent',
      },
      ...extraVariants,
    },
    variants: [
      {
        props: { variant: 'filled', color: 'primary' },
        style: {
          backgroundColor: palette.primary.light,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.primary.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[50],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'secondary' },
        style: {
          backgroundColor: palette.secondary.xlight,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.secondary.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[50],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'info' },
        style: {
          backgroundColor: palette.info.light,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.info.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[500],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'grey' },
        style: {
          backgroundColor: palette.grey[600],
          color: palette.grey[50],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.grey[600],
          },

          '& > svg': {
            color: palette.grey[50],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'error' },
        style: {
          backgroundColor: palette.error.light,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.error.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[50],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'success' },
        style: {
          backgroundColor: palette.success.light,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.success.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[50],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'warning' },
        style: {
          backgroundColor: palette.warning.light,
          color: palette.grey[600],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.warning.xlight,
            boxShadow: shadows[4],
          },

          '& > svg': {
            color: palette.grey[50],
          },

          // Series hint (?) icon
          '& > span:nth-of-type(2)': {
            backgroundColor: alpha(palette.background.paper, 0.16),
            borderRadius: '100%',
            color: palette.primary.contrastText,
            fontSize: typography.overline.fontSize,
            marginLeft: theme.spacing((1 / 2) * -1),
            paddingLeft: 0,
            textAlign: 'center',
            width: 17,

            '&:hover': {
              color: palette.primary.contrastText,
            },
          },
        },
      },
      {
        props: { variant: 'transparent' },
        style: {
          padding: 0,
          backgroundColor: 'transparent',
        },
      },
      {
        props: { variant: 'transparent', color: 'default' },
        style: {
          color: palette.text.primary,

          '&.MuiChip-clickable:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      {
        props: {
          variant: 'two-toned',
        },
        style: {
          color: palette.grey[50],
          display: 'inline-flex',
          height: 25,
          width: 'min-content',

          '& > span:first-of-type': {
            alignItems: 'center',
            backgroundColor: palette.grey[900],
            borderTopLeftRadius: radii.large,
            borderBottomLeftRadius: radii.large,
            display: 'flex',
            fontSize: 13,
            gap: theme.spacing(1 / 2),
            height: '100%',
            marginLeft: 0,
            marginRight: 0,
            padding: theme.spacing(0, 1),
            color: palette.grey[50],

            '& > svg': {
              fontSize: 14,
            },
          },

          '& > span:nth-of-type(2)': {
            fontSize: typography.overline.fontSize,
            lineHeight: typography.overline.lineHeight,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1 + 1 / 2),
          },

          // Clickable Hint (?) icon
          '& > span:nth-of-type(3)': {
            backgroundColor: alpha(palette.background.paper, 0.16),
            borderRadius: '100%',
            color: palette.info.contrastText,
            fontSize: typography.overline.fontSize,
            marginLeft: theme.spacing((1 / 2) * -1),
            paddingLeft: 0,
            textAlign: 'center',
            width: 17,

            '&:hover': {
              color: palette.info.contrastText,
            },
          },
        },
      },
      {
        props: {
          color: 'default',
          variant: 'two-toned',
        },
        style: {
          backgroundColor: palette.grey[900],

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.grey[900],
          },

          '& > span:first-of-type': {
            marginRight: theme.spacing((1 / 2) * -1),
          },
        },
      },
      {
        props: {
          color: 'error',
          variant: 'two-toned',
        },
        style: {
          backgroundColor: palette.error.xlight,
          color: palette.error.contrastText,

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.error.xlight,
          },
        },
      },
      {
        props: {
          color: 'primary',
          variant: 'two-toned',
        },
        style: {
          backgroundColor: palette.primary.xlight,
          color: palette.primary.contrastText,

          '&.MuiChip-clickable:hover': {
            backgroundColor: palette.primary.xlight,
          },
        },
      },
    ],
  },
};

export default Chip;
